import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { dateShort, money, moneySigned } from "../lib/format";
import StatusBadge from "./StatusBadge";
import NoteSelect from "./NoteSelect";
import { toast } from "sonner";
import { Loader2, FileText, Receipt, AlertTriangle, Calendar } from "lucide-react";

export default function TrackingDetailSheet({ reportId, tracking, onClose, onNoteChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tracking) {
      setData(null);
      return;
    }
    setLoading(true);
    api
      .getTracking(reportId, tracking)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [reportId, tracking]);

  const item = data?.item;
  const cmsRows = data?.cms_rows || [];
  const upsRows = data?.ups_rows || [];

  const saveNote = async (note) => {
    try {
      await api.updateNote(reportId, tracking, note);
      setData((prev) =>
        prev ? { ...prev, item: { ...prev.item, user_note: note } } : prev,
      );
      onNoteChange?.(tracking, note);
      toast.success(note ? `Note set: ${note}` : "Note cleared");
    } catch (e) {
      toast.error("Couldn't save note");
    }
  };

  return (
    <Sheet open={!!tracking} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        className="w-full sm:max-w-2xl overflow-y-auto"
        data-testid="tracking-detail-sheet"
      >
        <SheetHeader>
          <div className="eyebrow mb-2">Shipment</div>
          <SheetTitle className="font-display font-bold text-xl break-all font-mono-tabular">
            {tracking}
          </SheetTitle>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-[#525252] mt-8">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : !data ? (
          <div className="text-sm text-[#525252] mt-8">No data found.</div>
        ) : (
          <div className="mt-6 space-y-6 text-sm">
            {item && (
              <div className="border border-[#E5E5E5] rounded-md p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="eyebrow">Status</div>
                  <StatusBadge status={item.status} />
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#E5E5E5]">
                  <Stat
                    label="Ship date"
                    value={dateShort(item.ship_date)}
                    placeholder={
                      <span className="inline-flex items-center gap-1 text-[#DC2626]">
                        <AlertTriangle className="w-3 h-3" /> Missing in CMS
                      </span>
                    }
                  />
                  <Stat
                    label="Invoice #"
                    value={
                      item.ups_invoice_numbers?.length
                        ? item.ups_invoice_numbers.join(", ")
                        : null
                    }
                    placeholder={
                      <span className="inline-flex items-center gap-1 text-[#CA8A04]">
                        <AlertTriangle className="w-3 h-3" /> Not yet billed
                      </span>
                    }
                  />
                  <Stat
                    label="CMS charge"
                    value={item.cms_charge != null ? money(item.cms_charge) : null}
                    placeholder="—"
                  />
                  <Stat
                    label="UPS billed"
                    value={item.ups_billed_charge != null ? money(item.ups_billed_charge) : null}
                    placeholder="—"
                  />
                  <Stat
                    label="Profit / loss"
                    value={item.profit != null ? moneySigned(item.profit) : null}
                    placeholder="—"
                    accent={item.profit != null && item.profit < 0 ? "red" : null}
                  />
                  <Stat
                    label="Invoice date(s)"
                    value={
                      item.ups_invoice_dates?.length
                        ? item.ups_invoice_dates.join(", ")
                        : null
                    }
                    placeholder="—"
                  />
                </div>
                <div className="pt-3 border-t border-[#E5E5E5]">
                  <div className="text-[11px] uppercase tracking-widest text-[#525252] mb-2">
                    Internal note
                  </div>
                  <NoteSelect
                    value={item.user_note}
                    onChange={saveNote}
                    testId={`sheet-note-select-${tracking}`}
                  />
                </div>
                {item.notes && (
                  <div className="text-xs text-[#525252] pt-2 border-t border-[#E5E5E5]">
                    {item.notes}
                  </div>
                )}
              </div>
            )}

            <div>
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-3.5 h-3.5 text-[#525252]" />
                <span className="eyebrow">CMS records ({cmsRows.length})</span>
              </div>
              {cmsRows.length === 0 ? (
                <div className="border border-[#FECACA] bg-[#FFF0F0] text-[#DC2626] rounded-md px-3 py-2 text-xs flex items-center gap-2">
                  <AlertTriangle className="w-3 h-3" />
                  Flagged: this tracking number is NOT in your CMS report.
                </div>
              ) : (
                <ul className="space-y-2" data-testid="cms-rows">
                  {cmsRows.map((r, i) => (
                    <li
                      key={i}
                      className="border border-[#E5E5E5] rounded-md px-3 py-2 text-xs grid grid-cols-2 gap-2 font-mono-tabular"
                    >
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-[#525252]" />
                        {dateShort(r.ship_date)}
                      </span>
                      <span className="text-right">{money(r.charge)}</span>
                      <span className="text-[#525252] col-span-2">
                        {r.package_type || ""} {r.weight_lb ? `· ${r.weight_lb}lb` : ""} {r.dest_zip ? `· ZIP ${r.dest_zip}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2 mb-3">
                <Receipt className="w-3.5 h-3.5 text-[#525252]" />
                <span className="eyebrow">UPS invoice lines ({upsRows.length})</span>
              </div>
              {upsRows.length === 0 ? (
                <div className="border border-[#FEF08A] bg-[#FEFCE8] text-[#CA8A04] rounded-md px-3 py-2 text-xs flex items-center gap-2">
                  <AlertTriangle className="w-3 h-3" />
                  Not yet billed by UPS — no invoice line found.
                </div>
              ) : (
                <ul className="space-y-2" data-testid="ups-rows">
                  {upsRows.map((r, i) => (
                    <li
                      key={i}
                      className="border border-[#E5E5E5] rounded-md px-3 py-3 text-xs space-y-1"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono-tabular text-[#525252]">
                          Inv {r.invoice_number || "—"}
                        </span>
                        <span className="font-mono-tabular">
                          {dateShort(r.invoice_date)}
                        </span>
                      </div>
                      <div className="text-[#525252]">
                        {r.section || ""}
                        {r.service ? ` · ${r.service}` : ""}
                      </div>
                      <div className="grid grid-cols-3 gap-2 pt-1 border-t border-[#E5E5E5] font-mono-tabular">
                        <span>
                          <span className="text-[11px] uppercase text-[#525252] block">
                            Published
                          </span>
                          {money(r.published_charge)}
                        </span>
                        <span>
                          <span className="text-[11px] uppercase text-[#525252] block">
                            Incentive
                          </span>
                          {money(r.incentive_credit)}
                        </span>
                        <span className="font-medium">
                          <span className="text-[11px] uppercase text-[#525252] block font-normal">
                            Billed
                          </span>
                          {money(r.billed_charge)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Stat({ label, value, placeholder, accent }) {
  const accentCls = accent === "red" ? "text-[#DC2626]" : "";
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[#525252] mb-1">
        {label}
      </div>
      <div className={`font-mono-tabular text-sm ${accentCls}`}>
        {value != null ? value : placeholder}
      </div>
    </div>
  );
}
